import Checkbox from '@storeblocks/checkbox-atom';
import AlertInline from '@storeblocks/inline-alert';
import LinearLoadingIndicator from '@storeblocks/linear-loading-indicator';
import { ChangeEvent, type FC } from 'react';
import styled from 'styled-components';

import { LinkButton } from '@/components/shared/LinkButton';
import { Show } from '@/components/Show';
import { useHandleAsyncOperation } from '@/hooks/useHandleAsyncError';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';
import { stripFilename } from '@/util/stripFilename';

import { FileTypeIcon } from '../FileTypeIcon/FileTypeIcon';
import { WithGap } from '../WithGap';

interface Props {
  filename: string;
  fileId: string;
  fileExtension: string;
  checked: boolean;
  onCheck: (fileId: string, checked: boolean) => void;
  onDownload: (fileId: string) => Promise<unknown>;
}

export const FileNameColumnWithCheckbox: FC<Props> = ({
  filename,
  fileId,
  fileExtension,
  checked,
  onCheck,
  onDownload,
}: Props) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const handleCheck = (event: ChangeEvent<HTMLInputElement>): void => {
    onCheck(fileId, event.target.checked);
  };

  const startDownload = async (): Promise<void> => {
    await onDownload(fileId);
  };

  const [handleDownloadClick, isDownloading, isError] =
    useHandleAsyncOperation(startDownload);

  return (
    <Wrapper>
      <WithGap gap="16" direction="row">
        <Checkbox
          id={`file-${fileId}`}
          name={`file-download-checkbox-${fileId}`}
          checked={checked}
          value={fileId}
          label=""
          onChange={handleCheck}
        />

        <FileTypeIcon fileExtension={fileExtension} />

        <div>
          <Show when={!isDownloading}>
            <StyledLinkButton
              key="anchor"
              onClick={handleDownloadClick}
              data-trackid={getTrackId(
                'download-clicked-file',
                TrackingElement.Link,
              )}
              data-trackid-downloaded-file={stripFilename(filename)}
            >
              {filename}
            </StyledLinkButton>
          </Show>

          <Show when={isError}>
            <StyledInlineAlert
              variant="error"
              title={texts.download.singleFileError}
            />
          </Show>
        </div>

        <Show when={isDownloading}>
          <LinearLoadingWrapper>
            <LinearLoadingIndicator type="indeterminate" />
          </LinearLoadingWrapper>
        </Show>
      </WithGap>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledInlineAlert = styled(AlertInline)`
  padding-bottom: 0;
  padding-left: 0;
  align-items: center;

  & p {
    line-height: 1;
  }

  & svg {
    height: 20px;
    width: 20px;
  }
`;

const StyledLinkButton = styled(LinkButton)`
  margin-left: 0;
  word-break: break-all;
`;

const LinearLoadingWrapper = styled.div`
  justify-content: flex-start;
  align-self: center;
`;
